import React, { useEffect, useState } from "react";
import { CalendarDays, MapPin, Mic, Clock, Linkedin, Globe } from "lucide-react";
import { formatDateLong, formatDate, euro } from "@/lib/format";
import RegistrationPreview from "@/components/RegistrationPreview";
import { buildOverlapRows } from "@/lib/programmeOverlap";
import "./LandingDesign.css";

const CAT_TINTS = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#14b8a6", "#ef4444"];

function Countdown({ target, accent, id }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const remaining = new Date(target).getTime() - now;
  const diff = Number.isFinite(remaining) ? Math.max(0, remaining) : 0;
  const parts = [["Jours", Math.floor(diff / 86400000)], ["Heures", Math.floor((diff % 86400000) / 3600000)], ["Min", Math.floor((diff % 3600000) / 60000)], ["Sec", Math.floor((diff % 60000) / 1000)]];
  return <div className="jpi-countdown flex justify-center gap-3 sm:gap-5" data-testid={`countdown-values-${id}`} data-target={target}>{parts.map(([l, v]) => (
    <div key={l} className="flex flex-col items-center"><div data-testid={`countdown-${id}-${l.toLowerCase()}`} className="jpi-counter w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-2xl sm:text-3xl font-bold text-white" style={{ background: accent }}>{String(v).padStart(2, "0")}</div><span className="text-xs mt-2 text-slate-500 uppercase tracking-wide">{l}</span></div>
  ))}</div>;
}

function RegistrationDemo({ reg, accent }) {
  if (!reg || !(reg.tickets || []).length) return <p className="text-center text-slate-400">Formulaire d'inscription bientôt disponible.</p>;
  const color = reg.primaryColor || accent;
  const tickets = reg.tickets.filter((t) => !t.hidden);
  const fields = (tickets[0]?.fields) || ["Prénom", "Nom", "Email"];
  const customByLabel = Object.fromEntries((reg.customFields || []).map((c) => [c.label, c]));
  const Step = ({ n, title, children }) => (
    <div className="border border-slate-200 rounded-xl p-4 bg-white">
      <div className="flex items-center gap-2 mb-3"><span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ background: color }}>{n}</span><h4 className="font-semibold text-sm">{title}</h4></div>
      {children}
    </div>
  );
  return (
    <div className="space-y-3 max-w-lg mx-auto">
      <Step n={1} title="Choix des billets">
        {tickets.map((t) => <div key={t.id} className="flex justify-between items-center border border-slate-100 rounded-lg p-2.5 mb-2"><div><p className="font-medium text-sm">{t.name}</p><p className="text-xs text-slate-500">{t.description}</p></div><span className="font-bold" style={{ color }}>{t.price ? euro(t.price) : "Gratuit"}</span></div>)}
      </Step>
      <Step n={2} title="Coordonnées">
        <div className="grid grid-cols-2 gap-2">
          {fields.map((f) => { const cf = customByLabel[f]; if (cf?.type === "checkbox") return <label key={f} className="col-span-2 flex items-center gap-2 text-xs text-slate-600"><span className="h-3.5 w-3.5 rounded border border-slate-300" />{f}</label>; return <div key={f} className={`h-9 rounded-md border border-slate-200 bg-slate-50 px-2 flex items-center text-xs text-slate-400 ${["Email", "Entreprise", "Poste", "Ville / Code postal"].includes(f) ? "col-span-2" : ""}`}>{f}</div>; })}
        </div>
      </Step>
      {tickets.some((t) => t.price > 0) && <Step n={3} title="Paiement"><div className="h-9 rounded-md border border-slate-200 bg-slate-50 px-2 flex items-center text-xs text-slate-400 mb-2">N° de carte</div><div className="grid grid-cols-2 gap-2"><div className="h-9 rounded-md border border-slate-200 bg-slate-50 px-2 flex items-center text-xs text-slate-400">MM/AA</div><div className="h-9 rounded-md border border-slate-200 bg-slate-50 px-2 flex items-center text-xs text-slate-400">CVC</div></div></Step>}
      <Step n={tickets.some((t) => t.price > 0) ? 4 : 3} title="Confirmation"><p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3">✅ Inscription confirmée ! Un email récapitulatif vous sera envoyé.</p></Step>
    </div>
  );
}

function DesignedPage({ block, ctx }) {
  const d = block.design;
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(d.font)}:wght@400;500;600;700&family=${encodeURIComponent(d.headingFont)}&display=swap`;
    document.head.appendChild(link);
    return () => link.remove();
  }, [d.font, d.headingFont]);
  const variables = {
    "--jpi-font": `'${d.font}', sans-serif`, "--jpi-heading-font": `'${d.headingFont}', serif`,
    "--jpi-radius": `${d.radius}px`, "--jpi-spacing": `${d.spacing}px`,
    "--jpi-accent": d.accent, "--jpi-accent-ink": d.accentText,
    "--jpi-surface": d.surface, "--jpi-surface-ink": d.surfaceText,
    "--jpi-title-size": { compact: "2.5rem", large: "4rem", editorial: "5.5rem" }[d.headingScale],
  };
  return <div className="jpi-designed-page" style={variables} data-testid="block-ai-page" data-decoration={d.decoration}>
    {block.sourceBlocks.map((source, index) => {
      const section = d.sections?.find((s) => s.index === index) || {};
      const background = section.tone === "accent" ? d.accent : section.tone === "surface" ? d.surface : d.background;
      const ink = section.tone === "accent" ? d.accentText : section.tone === "surface" ? d.surfaceText : d.text;
      return <div key={`${source.id}-${index}`} className="jpi-designed-section" data-kind={source.type}
        data-layout={section.layout || "stack"} data-has-image={!!source.image}
        data-source-block-id={source.id} data-testid={`ai-source-block-${index}-${source.type}`}
        style={{ "--jpi-bg": background, "--jpi-ink": ink, "--jpi-align": section.align || "left" }}>
        <Block block={{ ...source, bg: background, accent: d.accent }} ctx={ctx} />
      </div>;
    })}
  </div>;
}

function AiPageBlock({ block, ctx }) {
  if (block.sourceBlocks && block.design) return <DesignedPage block={block} ctx={ctx} />;
  const html = block.html || "";
  // Split HTML on the registration placeholder so we can inject the real React form at the right spot.
  const MARK = /<div\s+id=["']jpi-registration["']\s*>\s*<\/div>/i;
  const parts = MARK.test(html) ? html.split(MARK) : [html, ""];
  const before = parts[0];
  const after = parts.slice(1).join("");
  const hasReg = parts.length > 1;
  const IframePart = ({ src, keyName }) => {
    const iframeRef = React.useRef(null);
    // Hauteur initiale généreuse (1400px) pour éviter le "saut" visuel au moment où
    // la vraie hauteur du contenu est mesurée. Cap max 12000px pour éviter les boucles
    // de feedback provoquées par les sections `min-height:100vh` dans le HTML IA
    // (viewport iframe = 100vh → sections grandissent avec l'iframe → scrollHeight
    // explose → runaway). NE JAMAIS observer de ResizeObserver ici.
    const MIN_H = 1400;
    const MAX_H = 12000;
    const [height, setHeight] = React.useState(MIN_H);
    React.useEffect(() => {
      const el = iframeRef.current;
      if (!el) return;
      let cancelled = false;
      const measure = () => {
        if (cancelled) return;
        try {
          const doc = el.contentDocument;
          if (!doc || !doc.body) return;
          const h = Math.max(
            doc.body.scrollHeight,
            doc.body.offsetHeight,
            MIN_H,
          );
          const target = Math.min(h + 24, MAX_H);
          // Ne jamais faire décroître (évite le "saut" court quand les polices arrivent).
          setHeight((prev) => (target > prev ? target : prev));
        } catch { /* cross-origin */ }
      };
      let timers = [];
      const onLoad = () => {
        try {
          const doc = el.contentDocument;
          if (!doc) return;
          // Mesures ponctuelles : après load, après ~1s (polices/images distantes), et fallback 3s.
          // AUCUN ResizeObserver = zéro feedback loop possible.
          timers.push(setTimeout(measure, 250));
          timers.push(setTimeout(measure, 1200));
          timers.push(setTimeout(measure, 3000));
          if (doc.fonts && doc.fonts.ready) {
            doc.fonts.ready.then(() => { if (!cancelled) measure(); }).catch(() => {});
          }
        } catch { /* cross-origin */ }
      };
      el.addEventListener("load", onLoad);
      return () => {
        cancelled = true;
        el.removeEventListener("load", onLoad);
        timers.forEach(clearTimeout);
      };
    }, [src]);
    return (
      <iframe
        ref={iframeRef}
        key={keyName}
        title={`AI landing ${keyName}`}
        srcDoc={src || "<!doctype html><html><body></body></html>"}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation"
        referrerPolicy="no-referrer"
        style={{
          width: "100%",
          height: `${height}px`,
          border: 0,
          display: "block",
          background: "transparent",
          minHeight: `${MIN_H}px`,
          maxHeight: `${MAX_H}px`,
        }}
      />
    );
  };
  return (
    <section data-testid="block-ai-page" style={{ background: "#000" }}>
      <IframePart src={before} keyName="before" />
      {hasReg && (
        <div style={{ background: block.bg || "#0f172a" }}>
          <RegistrationPreview event={ctx.event} cfg={ctx.registration} slug={ctx.slug} interactive={ctx.interactive} />
        </div>
      )}
      {hasReg && after.trim() && <IframePart src={after} keyName="after" />}
    </section>
  );
}

function Block({ block, ctx }) {
  const { event, days, activities, registration, interactive, slug, company } = ctx;
  const accent = block.accent || event?.color || "#6366f1";
  const bg = block.bg || "#ffffff";
  const catColors = {};
  let ci = 0;
  const actsOf = (dayId) => (activities || []).filter((a) => a.dayId === dayId).sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
  const groupedActivities = (dayId) => buildOverlapRows(actsOf(dayId));

  switch (block.type) {
    case "hero":
      return (
        <section className="relative min-h-[420px] flex flex-col justify-center items-center text-center px-6 py-20 text-white overflow-hidden" data-testid="block-hero">
          <div className="absolute inset-0" style={{ background: block.image ? undefined : `linear-gradient(135deg, ${accent}, #0f172a)` }}>{block.image && <img src={block.image} alt="" className="h-full w-full object-cover" />}</div>
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,23,42,.25), rgba(15,23,42,.75))" }} />
          <div className="relative max-w-3xl">
            <h1 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight">{block.title || event?.name}</h1>
            {block.text && <p className="mt-4 text-lg text-white/90">{block.text}</p>}
            <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
              {event?.startDate && <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-2 rounded-full"><CalendarDays className="h-4 w-4" />{formatDateLong(event.startDate)}</span>}
              {event?.city && <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-2 rounded-full"><MapPin className="h-4 w-4" />{event.city}</span>}
            </div>
          </div>
        </section>
      );
    case "text":
      return (
        <section className="px-6 py-10" style={{ background: bg }} data-testid="block-text">
          <div className="max-w-3xl mx-auto leading-relaxed text-base [&_a]:text-indigo-600 [&_a]:underline" style={{ textAlign: block.align || "left" }}>
            {block.title && <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3" style={{ color: accent }}>{block.title}</h2>}
            {block.html ? <div dangerouslySetInnerHTML={{ __html: block.html }} /> : <p className="whitespace-pre-line">{block.text}</p>}
          </div>
        </section>
      );
    case "image":
      return (
        <section className="px-6 py-8" style={{ background: bg }} data-testid="block-image">
          <div className="max-w-4xl mx-auto" style={{ textAlign: block.align || "center" }}>
            {block.image && (block.url
              ? <a href={block.url} target="_blank" rel="noreferrer"><img src={block.image} alt={block.title || ""} className="inline-block rounded-2xl max-w-full cursor-pointer" style={{ width: `${block.width || 100}%` }} /></a>
              : <img src={block.image} alt={block.title || ""} className="inline-block rounded-2xl max-w-full" style={{ width: `${block.width || 100}%` }} />)}
          </div>
        </section>
      );
    case "gallery":
      return (
        <section className="px-6 py-12" style={{ background: bg }} data-testid="block-gallery">
          <div className="max-w-5xl mx-auto">
            {block.title && <h2 className="font-display text-3xl font-bold mb-6 text-center" style={{ color: accent }}>{block.title}</h2>}
            <div className="jpi-item-list grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(block.items || []).map((it, i) => { const img = <img src={it.image} alt={it.caption || it.title || ""} className="w-full h-40 object-cover rounded-xl" />; return <figure key={i}>{it.link ? <a href={it.link} target="_blank" rel="noreferrer" data-testid={`gallery-link-${block.id}-${i}`}>{img}</a> : img}{(it.caption || it.title) && <figcaption className="mt-2 text-sm">{it.caption || it.title}</figcaption>}</figure>; })}
            </div>
          </div>
        </section>
      );
    case "button":
      return (
        <section className="px-6 py-8" style={{ background: bg }} data-testid="block-button">
          <div className="max-w-3xl mx-auto" style={{ textAlign: block.align || "center" }}>
            <a href={block.url || "#"} target="_blank" rel="noreferrer" className="inline-block px-8 py-3 rounded-xl font-semibold" style={{ background: accent, color: block.color || "#ffffff" }}>{block.label || "En savoir plus"}</a>
          </div>
        </section>
      );
    case "video":
      return (
        <section className="px-6 py-8" style={{ background: bg }} data-testid="block-video">
          <div className="max-w-3xl mx-auto">{block.title && <h2 className="font-display text-2xl font-bold mb-4 text-center" style={{ color: accent }}>{block.title}</h2>}{block.url && <video controls src={block.url} className="w-full rounded-2xl" />}</div>
        </section>
      );
    case "youtube": {
      const m = (block.url || "").match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
      const id = m ? m[1] : (block.url || "");
      return (
        <section className="px-6 py-8" style={{ background: bg }} data-testid="block-youtube">
          <div className="max-w-3xl mx-auto">{block.title && <h2 className="font-display text-2xl font-bold mb-4 text-center" style={{ color: accent }}>{block.title}</h2>}{id && <div className="relative w-full" style={{ paddingBottom: "56.25%" }}><iframe title="YouTube" src={`https://www.youtube.com/embed/${id}`} className="absolute inset-0 w-full h-full rounded-2xl" allowFullScreen frameBorder="0" /></div>}</div>
        </section>
      );
    }
    case "maps":
      return (
        <section className="px-6 py-8" style={{ background: bg }} data-testid="block-maps">
          <div className="max-w-3xl mx-auto">{block.title && <h2 className="font-display text-2xl font-bold mb-4 text-center" style={{ color: accent }}>{block.title}</h2>}{(block.query || event?.city) && <iframe title="Maps" src={`https://www.google.com/maps?q=${encodeURIComponent(block.query || `${event?.address || ""} ${event?.city || ""}`)}&output=embed`} className="w-full h-72 rounded-2xl border-0" />}</div>
        </section>
      );
    case "programme":
      return (
        <section className="px-6 py-16" style={{ background: bg }} data-testid="block-programme">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-display text-3xl font-bold mb-8 text-center" style={{ color: accent }}>{block.title || "Programme"}</h2>
            {(days || []).length ? [...days].sort((a, b) => a.dayNumber - b.dayNumber).map((day) => {
              return (
                <div key={day.id} className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex flex-col items-center justify-center w-14 h-14 rounded-2xl text-white shrink-0" style={{ background: accent }}><span className="text-[9px] uppercase tracking-wide opacity-90">Jour</span><b className="text-xl leading-none">{day.dayNumber}</b></div>
                    <p className="font-display font-bold text-lg capitalize">{formatDate(day.date)}</p>
                  </div>
                  {actsOf(day.id).length ? (
                    <div className="pl-3 border-l-2 border-slate-100 space-y-3">
                      {groupedActivities(day.id).map((group, groupIndex) => (
                        <div key={`${group.startTime}-${groupIndex}`} className="flex gap-4" data-testid={`public-programme-group-${day.id}-${groupIndex}`}>
                          <div className="w-16 text-right shrink-0"><p className="font-bold text-sm">{group.startTime || "--:--"}</p></div>
                          <div className={`flex-1 grid grid-cols-1 gap-3 ${group.items.length === 1 ? "" : group.items.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid={`public-programme-columns-${day.id}-${groupIndex}`}>
                        {group.items.map(({ activity: a, isContinuation }) => {
                          const cat = a.category || "Général";
                          if (!catColors[cat]) catColors[cat] = CAT_TINTS[ci++ % CAT_TINTS.length];
                          const tint = catColors[cat];
                          return (
                            <div key={`${a.id}-${group.start}`} className="jpi-card h-40 rounded-xl border border-slate-200 p-3 bg-white min-w-0 overflow-auto" data-testid={`public-programme-activity-${a.id}-${group.start}`} style={{ borderLeft: `3px solid ${tint}` }}>
                              {a.location && <p className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: tint }}>{a.location}</p>}
                              <div className="flex items-start justify-between gap-2"><p className="font-semibold text-sm">{a.title}{isContinuation ? " (suite)" : ""}</p><span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase" style={{ background: `${tint}1a`, color: tint }}>{cat}</span></div>
                              {a.description && <p className="text-xs text-slate-500 mt-1">{a.description}</p>}
                              <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">{a.endTime && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Fin {a.endTime}</span>}{a.speaker && <span className="flex items-center gap-1"><Mic className="h-3 w-3" />{a.speaker}</span>}</div>
                            </div>
                          );
                        })}
                          </div>
                        </div>
                      ))}
                      </div>
                  ) : <p className="text-sm text-slate-400">Programme à venir.</p>}
                </div>
              );
            }) : <p className="text-center text-slate-400 flex items-center justify-center gap-2"><Clock className="h-4 w-4" />Programme bientôt disponible.</p>}
          </div>
        </section>
      );
    case "speakers":
      return (
        <section className="px-6 py-16" style={{ background: bg }} data-testid="block-speakers">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-display text-3xl font-bold mb-8 text-center" style={{ color: accent }}>{block.title || "Intervenants"}</h2>
            <div className="jpi-item-list space-y-4">
              {(block.items || []).map((it, i) => (
                <div key={i} className="jpi-card flex gap-4 items-start p-4 rounded-2xl border border-slate-200 bg-white" data-testid={`speaker-${block.id}-${i}`}>
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-100 shrink-0">{it.photo && <img src={it.photo} alt="" className="h-full w-full object-cover" />}</div>
                  <div className="min-w-0">
                    <p className="font-display font-bold">{it.name}</p>
                    {it.role && <p className="text-sm font-medium" style={{ color: accent }}>{it.role}</p>}
                    {it.bio && <p className="text-sm text-slate-500 mt-1 whitespace-pre-line" data-testid={`speaker-bio-${block.id}-${i}`}>{it.bio}</p>}
                    {it.description && <p className="text-sm text-slate-500 mt-1 whitespace-pre-line" data-testid={`speaker-description-${block.id}-${i}`}>{it.description}</p>}
                    <div className="flex gap-3 mt-2">
                      {it.linkedin && <a href={it.linkedin} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-indigo-600" aria-label="LinkedIn" data-testid={`speaker-linkedin-${block.id}-${i}`}><Linkedin className="h-4 w-4" /></a>}
                      {it.website && <a href={it.website} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-indigo-600" aria-label="Site web" data-testid={`speaker-website-${block.id}-${i}`}><Globe className="h-4 w-4" /></a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    case "exposants":
      return (
        <section className="px-6 py-16" style={{ background: bg }} data-testid="block-exposants">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-3xl font-bold mb-8 text-center" style={{ color: accent }}>{block.title || "Exposants"}</h2>
            <div className="jpi-item-list grid sm:grid-cols-2 gap-4">
              {(block.items || []).map((it, i) => (
                <div key={i} className="jpi-card flex gap-4 items-start p-4 rounded-2xl border border-slate-200 bg-white">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center">{it.logo ? <img src={it.logo} alt="" className="h-full w-full object-contain" /> : <span className="text-slate-300 text-xs">Logo</span>}</div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap"><p className="font-display font-bold">{it.name}</p>{it.stand && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${accent}1a`, color: accent }}>Stand {it.stand}</span>}</div>
                    {it.description && <p className="text-sm text-slate-500 mt-1">{it.description}</p>}
                    {it.website && <a href={it.website} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-indigo-600 inline-flex items-center gap-1 mt-2 text-xs"><Globe className="h-3.5 w-3.5" />Site web</a>}
                  </div>
                </div>
              ))}
              {!(block.items || []).length && <p className="text-center text-slate-400 col-span-2">Exposants à venir.</p>}
            </div>
          </div>
        </section>
      );
    case "sponsors":
      return (
        <section className="px-6 py-16" style={{ background: bg }} data-testid="block-sponsors">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl font-bold mb-8" style={{ color: accent }}>{block.title || "Nos partenaires"}</h2>
            <div className="flex flex-wrap justify-center items-center gap-8">
              {(block.items || []).map((it, i) => { const inner = <span className="flex flex-col items-center gap-2">{it.logo && <img src={it.logo} alt={it.name || ""} className="max-h-16 object-contain" />}<span className="text-lg font-semibold">{it.name}</span></span>; return <div key={i} data-testid={`sponsor-${block.id}-${i}`}>{it.website ? <a href={it.website} target="_blank" rel="noreferrer" data-testid={`sponsor-link-${block.id}-${i}`}>{inner}</a> : inner}</div>; })}
            </div>
          </div>
        </section>
      );
    case "countdown":
      return (
        <section className="px-6 py-16 text-center" style={{ background: bg }} data-testid="block-countdown">
          <h2 className="font-display text-3xl font-bold mb-8" style={{ color: accent }}>{block.title || "Compte à rebours"}</h2>
          {(block.date || event?.startDate) ? <Countdown target={block.date || event.startDate} accent={accent} id={block.id} /> : <p className="text-slate-400">Date à venir</p>}
        </section>
      );
    case "registration":
      return (
        <section className="px-6 py-16" style={{ background: bg }} data-testid="block-registration">
          <div className="max-w-xl mx-auto text-center mb-6">
            <h2 className="font-display text-3xl font-bold mb-3" style={{ color: accent }}>{block.title || "Inscription"}</h2>
            {block.text && <p className="text-slate-500">{block.text}</p>}
          </div>
          <div className="max-w-lg mx-auto">
            <RegistrationPreview cfg={registration || { tickets: [], primaryColor: accent }} event={event} guests={[]} interactive={!!(interactive && slug)} slug={slug} />
          </div>
        </section>
      );
    case "faq":
      return (
        <section className="px-6 py-16" style={{ background: bg }} data-testid="block-faq">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-display text-3xl font-bold mb-8 text-center" style={{ color: accent }}>{block.title || "Questions fréquentes"}</h2>
            <div className="space-y-3">{(block.items || []).map((it, i) => (<details key={i} className="jpi-card border border-slate-200 rounded-xl p-4 bg-white" data-testid={`faq-${block.id}-${i}`}><summary className="font-semibold cursor-pointer" data-testid={`faq-question-${block.id}-${i}`}>{it.q}</summary><p className="text-sm text-slate-600 mt-2">{it.a}</p></details>))}</div>
          </div>
        </section>
      );
    case "divider":
      return <div style={{ background: bg }} data-testid="block-divider"><div className="max-w-3xl mx-auto py-6"><div className="border-t border-slate-200" /></div></div>;
    case "footer":
      return (
        <footer className="px-6 py-10 text-center text-white" style={{ background: block.bg && block.bg !== "#ffffff" ? block.bg : "#0f172a" }} data-testid="block-footer">
          <p className="font-display font-bold text-lg">{event?.name}</p>
          {block.text && <p className="text-sm text-white/70 mt-2 whitespace-pre-line">{block.text}</p>}
          {company?.companyName && <p className="text-sm text-white/80 mt-3" data-testid="landing-organizer-details">{company.companyName}{[company.headquartersAddress, company.headquartersPostal, company.headquartersCity].filter(Boolean).length ? ` · ${[company.headquartersAddress, company.headquartersPostal, company.headquartersCity].filter(Boolean).join(", ")}` : ""}</p>}
          <p className="text-xs text-white/40 mt-4">Propulsé par JustPlanIt</p>
        </footer>
      );
    default:
      return null;
  }
}

export default function LandingView({ blocks, ctx }) {
  return <div className="bg-white">{(blocks || []).map((b) => b.type === "ai_page"
    ? <AiPageBlock key={b.id} block={b} ctx={ctx} />
    : <Block key={b.id} block={b} ctx={ctx} />)}</div>;
}